import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { sendEvent } from '@alfa-bank/site-helpers-common/browser/send-event';

import type { IOnChangeCallback } from '../../../models';
import { useTabsHeader } from '../tabs-header/hooks/use-tabs-header';
import type { TPanelHeaderDesktopV2Props } from '../panel-header-v2/models';
import type { TPanelHeaderMobileProps } from '../panel-header/models';
import { Progress } from '../../progress';
import { useStandardAnchorScroll } from '../tags-header/hooks/use-standard-anchor-scroll';

export const useTabsPanel = ({
    autoSwitchTime,
    onAutoChange,
    onChange,
    selectedID,
    theme = 'light',
    titles,
}: Pick<
    TPanelHeaderMobileProps | TPanelHeaderDesktopV2Props,
    'autoSwitchTime' | 'onChange' | 'selectedID' | 'theme' | 'titles' | 'onAutoChange'
>) => {
    const renderProgress = useMemo(
        () => <Progress autofillTime={autoSwitchTime} theme={theme} />,
        [autoSwitchTime, theme]
    );

    const [autoSwitch, setAutoSwitch] = useState<boolean>(!!autoSwitchTime);
    const [debouncedProgress, setDebouncedProgress] = useState<JSX.Element | null>(null);

    useStandardAnchorScroll();

    const titleIds = useMemo(() => titles.map(item => item.id), [titles]);

    useEffect(() => {
        // Таймаут чтобы пересчиталась ширина элемента прогресса, при равной ширине табов (widthTab = 'equal')
        const timeout = setTimeout(() => setDebouncedProgress(renderProgress));

        return () => clearTimeout(timeout);
    }, [renderProgress]);

    const autoSwitcher = useCallback(
        (id: string | number, switchAnalytics?: { [key: string]: string }) => {
            sendEvent(switchAnalytics);
            onChange({ id });
        },
        [onChange]
    );

    useEffect(() => {
        if (autoSwitch) {
            const switchTimeout = setTimeout(() => {
                let nextIdx = titleIds.indexOf(selectedID || titles[0].id) + 1;
                if (nextIdx >= titles.length) {
                    nextIdx = 0;
                }
                autoSwitcher(titles[nextIdx].id, titles[nextIdx].analytics?.switch);
            }, autoSwitchTime);

            return () => clearTimeout(switchTimeout);
        }
    }, [autoSwitch, autoSwitchTime, autoSwitcher, selectedID, titleIds, titles]);

    const handleOnChange = useCallback(
        (params: IOnChangeCallback) => {
            if (autoSwitch) {
                onAutoChange(params);
            }
            onChange(params);
            setAutoSwitch(false);
        },
        [autoSwitch, onAutoChange, onChange]
    );
    const { alignStyle, headerContainer, headerList, selectedTab, withIcon, ...tabsProps } =
        useTabsHeader({
            onChange: handleOnChange,
            titles,
            theme,
            selectedID,
        });

    useEffect(() => {
        if (selectedTab.current && headerContainer.current) {
            const { offsetLeft, offsetWidth } = selectedTab.current;
            const headerListWidth = headerContainer.current.offsetWidth ?? 0;

            headerContainer.current?.scroll({
                behavior: 'smooth',
                left: offsetLeft - Math.round(headerListWidth / 2) + Math.round(offsetWidth / 2),
            });
        }
    }, [headerContainer, headerList, selectedTab, selectedID]);

    return {
        autoSwitch,
        debouncedProgress,
        headerContainer,
        headerList,
        selectedTab,
        tabsProps,
        withIcon,
    };
};
